import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../../contexts/user.context';
import { mobilizeiSubscriptionsService } from '../../../../services/mobilizei/subscriptions.service';
import { automaticPixStatus, plans, subscriptionStatus } from '../../../../utils/constants';
import { formatIsoTimestamp } from '../../../../utils/mappers';

export default function AutomaticPixPaymentMethod() {
    const { user } = useUser();
    const navigate = useNavigate();
    const [pendingInvoiceId, setPendingInvoiceId] = useState(null);

    const subscription = user?.subscription;
    const isCancelled = [subscriptionStatus.PENDING_SUSPENSION, subscriptionStatus.SUSPENDED].includes(subscription?.status);
    const status = isCancelled ? automaticPixStatus.CANCELLED : subscription?.automaticPix?.status;
    const isAuthorized = status === automaticPixStatus.AUTHORIZED;

    const renewalDate = formatIsoTimestamp(subscription?.expiresAt, 'DD/MM/YYYY');
    const price = plans[subscription?.planId]?.totalPrice;

    // When the Pix Automático is not authorized, the customer has to pay (and authorize)
    // through the QR Code of the open invoice, so we look for it
    useEffect(() => {
        if (isAuthorized || isCancelled || !subscription?.id) {
            return;
        }

        mobilizeiSubscriptionsService.getInvoices(subscription.id)
            .then(invoices => {
                const pendingInvoice = (invoices || []).find(i => i.status === 'pending');
                setPendingInvoiceId(pendingInvoice?.id || null);
            })
            .catch(() => null);
    }, [subscription?.id, isAuthorized, isCancelled]);

    const getStatusChip = () => {
        switch (status) {
            case automaticPixStatus.AUTHORIZED:
                return <Chip label="Ativo" color="success" size="small" />
            case automaticPixStatus.PENDING_AUTHORIZATION:
                return <Chip label="Aguardando autorização" color="warning" size="small" />
            case automaticPixStatus.CANCELLED:
                return <Chip label="Cancelado" size="small" />
            default:
                return <Chip label="Não autorizado" color="error" size="small" />
        }
    }

    const getStatusMessage = () => {
        switch (status) {
            case automaticPixStatus.AUTHORIZED:
                return 'O valor da sua assinatura é debitado automaticamente da sua conta a cada renovação. Você não precisa fazer nada.'
            case automaticPixStatus.PENDING_AUTHORIZATION:
                return 'Para ativar o débito automático, pague a fatura em aberto e autorize o Pix Automático no app do seu banco. Se você já autorizou, a confirmação pode levar alguns minutos.'
            case automaticPixStatus.CANCELLED:
                return 'O Pix Automático da sua assinatura foi cancelado e nenhum novo débito será realizado.'
            default:
                return 'O débito automático não está ativo. Na próxima renovação você receberá por e-mail um novo QR Code para pagar e autorizar o Pix Automático.'
        }
    }

    return (
        <Card
            elevation={2}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                justifyContent: 'center',
                padding: 1
            }}
        >
            <CardContent>
                <Box alignItems="center" display="flex" justifyContent="space-between" mb={2}>
                    <Box alignItems="center" display="flex">
                        <img
                            src={require('../../../../assets/img/billing/pix_selected.png')}
                            alt="Pix Automático"
                            style={{ width: 32, marginRight: 12 }}
                        />
                        <Typography variant="h6">
                            Pix Automático
                        </Typography>
                    </Box>
                    {getStatusChip()}
                </Box>

                <Typography variant="body1" color="textSecondary">
                    {getStatusMessage()}
                </Typography>

                {isAuthorized && renewalDate &&
                    <Typography variant="body1" mt={2}>
                        Próximo débito: <b>{renewalDate}</b>
                        {price ? <> (R$ {price},00)</> : null}
                    </Typography>
                }

                {!isAuthorized && pendingInvoiceId &&
                    <Box mt={2}>
                        <Button
                            size="medium"
                            variant="contained"
                            onClick={() => navigate(`/faturas/${pendingInvoiceId}`)}
                        >
                            Ver fatura em aberto
                        </Button>
                    </Box>
                }
            </CardContent>
        </Card>
    );
}
